import { useEffect } from 'react';
import { CreateStartupForm } from '@/features/startups/components/CreateStartupForm';
import { StartupAlreadyExistsState } from '@/features/startups/components/StartupAlreadyExistsState';
import { EmailReplacementRequiredState } from '@/features/startups/components/EmailReplacementRequiredState';
import { useStartupEligibility } from '@/features/startups/hooks/useStartupEligibility';
import { CREATE_STARTUP_STRINGS } from '@/features/startups/constants';
import { PageHeader } from '@/components/common/PageHeader';
import { UserChip } from '@/components/common/UserChip';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { clearPurchaseIntent } from '@/lib/auth/intent-storage';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { getApiErrorCode, getApiErrorDetail } from '@/lib/utils/error.utils';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { displayNameFromEmail } from '@/lib/utils/user.utils';

export function CreateStartupPage() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  // Session claims carry no full name; use the person's profile name, and only
  // until it loads (or if it is empty) fall back to the email local-part (the
  // same interim rule as the navbar initials).
  const profileName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '';
  const displayName = profileName || (user ? displayNameFromEmail(user.email) : '');

  const eligibility = useStartupEligibility();
  // Only the two known codes gate the wizard - any other failure (e.g. a
  // network blip) falls through to the form instead, since this check is a
  // UX shortcut, not the source of truth (sendWorkEmailOtp/createStartup
  // re-check the same rules server-side regardless).
  // `checkEligibility` rejects on a personal email before it ever checks the
  // domain, so the two codes cannot both come back from one call - the
  // ordering here just mirrors that same precedence (FR-14, MYN-76).
  const requiresWorkEmail =
    eligibility.isError && getApiErrorCode(eligibility.error) === API_ERROR_CODES.WORK_EMAIL_REQUIRED;
  const alreadyExists =
    eligibility.isError &&
    getApiErrorCode(eligibility.error) === API_ERROR_CODES.STARTUP_DOMAIN_ALREADY_REGISTERED;

  // The duplicate-startup block ends the Issue Report journey there, so a later
  // login must not be sent back into it (FR-206).
  useEffect(() => {
    if (alreadyExists) clearPurchaseIntent();
  }, [alreadyExists]);

  return (
    <div className="min-h-full bg-page-bg">
      <PageHeader
        title={CREATE_STARTUP_STRINGS.PAGE_TITLE}
        rightSlot={
          displayName ? (
            <UserChip name={displayName} />
          ) : undefined
        }
      />
      {eligibility.isLoading ? (
        <div className="flex justify-center py-24">
          <LoadingSpinner />
        </div>
      ) : requiresWorkEmail ? (
        <EmailReplacementRequiredState />
      ) : alreadyExists ? (
        <StartupAlreadyExistsState existingCompanyName={getApiErrorDetail(eligibility.error)} />
      ) : (
        // Figma: Step 1 cards span 1312px; the stepper and steps 2-4 use a
        // centered 760px column inside it (widths measured from the frames).
        <div className="mx-auto w-full max-w-328 px-4 py-10">
          <CreateStartupForm />
        </div>
      )}
    </div>
  );
}
